import { z } from "zod";
import Cookies from "js-cookie";
import { API_URL, SALT } from "lib/constants";
import { argon2id } from "lib/utils";
import { sendHttpRequest } from "./fetcher";
import { AxiosRequestConfig } from "axios";
import { message } from "antd";
import { NumberFound } from "libphonenumber-js";

const phoneRegex = new RegExp(
  /^([+]?[\s0-9]+)?(\d{3}|[(]?[0-9]+[)])?([-]?[\s]?[0-9])+$/,
);

const FormSchemaCustomer = z.object({
  name: z.string().trim().min(1, { message: "Please enter customer name." }),
  email: z.string().email({ message: "Please enter valid email address." }),
  first_name: z.string().trim().min(1, { message: "Please enter first name." }),
  last_name: z.string().trim().min(1, { message: "Please enter last name." }),
  phone: z.string().trim(),
});

const CreateCustomer = FormSchemaCustomer.omit({});
const UpdateCustomer = FormSchemaCustomer.omit({});

const FormSchemaSite = z.object({
  orgId: z.string(),
  name: z.string().trim().min(1, { message: "Please enter site name." }),
  address: z.string().trim().min(1, { message: "Please enter address." }),
  city: z.string().trim().min(1, { message: "Please select a specific city." }),
  state: z
    .string()
    .trim()
    .min(1, { message: "Please select a specific state." }),
  zip: z.string().trim().min(1, { message: "Please select a specific zip." }),
  timezone: z.string().trim().min(1, { message: "Please select a timezone." }),
});

const CreateSite = FormSchemaSite.omit({ orgId: true });

const FormSchemaNvr = z.object({
  orgId: z.string(),
  siteId: z.string(),
  macAddress: z
    .string()
    .trim()
    .min(1, { message: "Please enter MAC address." }),
  serialNumber: z
    .string()
    .trim()
    .min(1, { message: "Please enter serial number." }),
});

const CreateNvr = FormSchemaNvr.omit({ orgId: true, siteId: true });

const FormSchemaCamera = z.object({
  orgId: z.string(),
  siteId: z.string(),
  nvrId: z.string(),
  postData: z.string(),
});

const CreateCamera = FormSchemaCamera.omit({
  orgId: true,
  siteId: true,
  nvrId: true,
  postData: true,
});

const FormSchemaActivateDealer = z.object({
  token: z.string(),
  password: z.string().trim().min(1, { message: "Please enter password." }),
  confirm_password: z
    .string()
    .trim()
    .min(1, { message: "Please enter confirm password." }),
});

const ActivateDealer = FormSchemaActivateDealer.omit({ token: true });

const FormSchemaSignin = z.object({
  email: z.string().email({ message: "Please enter valid email address." }),
  password: z.string().trim().min(1, { message: "Please enter password." }),
});
const Signin = FormSchemaSignin.omit({});
const FormSchemaResetEmail = z.object({
  email: z.string().email({ message: "Please enter valid email address." }),
});
const ResetEmail = FormSchemaResetEmail.omit({});

// This is temporary until @types/react-dom is updated

export type StateGeneral = {
  errors?: any;
  message?: string | null;
};

export type StateCustomer = {
  errors?: {
    name?: string[];
    email?: string[];
    first_name?: string[];
    last_name?: string[];
    phone?: string[];
  };
  message?: string | null;
  strReturn?: any | null;
};

export type StateSite = {
  errors?: {
    name?: string[];
    address?: string[];
    city?: string[];
    state?: string[];
    zip?: string[];
    timezone?: string[];
  };
  message?: string | null;
  strReturn?: any | null;
};

export type StateNvr = {
  errors?: {
    macAddress?: string[];
    serialNumber?: string[];
  };
  message?: string | null;
  strReturn?: any | null;
};

export type StateCamera = {
  errors?: any;
  message?: string | null;
  strReturn?: string | null;
};

export type StateActivateDealer = {
  errors?: {
    // token?: string[];
    password?: string[];
    confirm_password?: string[];
  };
  message?: string | null;
};

export type StateSignin = {
  errors?: {
    email?: string[];
    password?: string[];
  };
  message?: string | null;
};

export async function createCustomer(formData: FormData) {
  const validatedFields = CreateCustomer.safeParse({
    name: formData.get("name"),
    email: formData.get("email"),
    first_name: formData.get("first_name"),
    last_name: formData.get("last_name"),
    phone: formData.get("phone"),
  });

  if (validatedFields.success === false) {
    return {
      errors: validatedFields.error.flatten().fieldErrors,
      message: "Missing Fields. Failed to Create Customer.",
    };
  }

  const { name, email, first_name, last_name, phone } = validatedFields.data;

  const token = Cookies.get("token");

  try {
    const response = await sendHttpRequest({
      url: `${API_URL}/user/customers`,
      method: "post",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      data: JSON.stringify({
        accountName: name,
        email: email,
        firstName: first_name,
        lastName: last_name,
        phoneNumber: phone,
      }),
    });

    if (response.status !== 200) {
      throw new Error(response?.data?.message);
    }

    return {
      strReturn: response.data.data.user.organization.id,
    };
  } catch (error) {
    return {
      message: `${error}`,
    };
  }
}

export async function getCustomer(orgId: string, name: string) {
  const token = Cookies.get("token");

  try {
    const response = await sendHttpRequest({
      url: `${API_URL}/user/customers?name=${name}`,
      method: "get",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        "x-org-id": orgId,
      },
    });
    if (response.status !== 200) {
      throw new Error(response?.data?.message);
    }
    const result = response?.data;
    return result;
  } catch (error) {
    return {
      message: "Database Error: Failed to get Camera.",
    };
  }
}

export async function updateCustomer(orgId: string, formData: FormData) {
  const validatedFields = UpdateCustomer.safeParse({
    name: formData.get("name"),
    email: formData.get("email"),
    first_name: formData.get("first_name"),
    last_name: formData.get("last_name"),
    phone: formData.get("phone"),
  });

  if (validatedFields.success === false) {
    return {
      errors: validatedFields.error.flatten().fieldErrors,
      message: "Missing Fields. Failed to update Customer.",
    };
  }

  const { name, first_name, last_name, phone } = validatedFields.data;

  const token = Cookies.get("token");

  try {
    const response = await sendHttpRequest({
      url: `${API_URL}/user/customers/${orgId}`,
      method: "put",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      data: JSON.stringify({
        accountName: name,
        // "company": {
        //     "name": "test_company"
        // },
        // "email": email,
        firstName: first_name,
        lastName: last_name,
        phoneNumber: phone,
      }),
    });
    if (response.status !== 200) {
      throw new Error(response?.data?.message);
    }
    const result = response?.data;

    console.log("--- result = ---", result, result?.message);

    return {
      // strReturn: result.data.user.organization.id
      strReturn: "ok",
    };
  } catch (error) {
    return {
      message: `${error}`,
    };
  }
}

export async function deleteCustomer(orgId: string) {
  const token = Cookies.get("token");

  try {
    const response = await sendHttpRequest({
      url: `${API_URL}/user/customers/${orgId}`,
      method: "delete",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      data: JSON.stringify({}),
    });

    if (response.status !== 200) {
      throw new Error(response?.data?.message);
    }

    return response?.data;
  } catch (error) {
    throw new Error(`Failed to delete a customer ${error}`);
  }
}

export async function createSite(orgId: string, formData: FormData) {
  const validatedFields = CreateSite.safeParse({
    name: formData.get("name"),
    address: formData.get("address"),
    city: formData.get("city"),
    state: formData.get("state"),
    zip: formData.get("zip"),
    lat: formData.get("lat"),
    lng: formData.get("lng"),
    timezone: formData.get("timezone"),
  });

  if (validatedFields.success === false) {
    return {
      errors: validatedFields.error.flatten().fieldErrors,
      message: "Missing Fields. Failed to Create Site.",
    };
  }

  const { name, address, city, state, zip, timezone } = validatedFields.data;

  const token = Cookies.get("token");

  const lat = formData.get("latitude");
  const lng = formData.get("longitude");

  try {
    const response = await sendHttpRequest({
      url: `${API_URL}/site/sites`,
      method: "post",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        "x-org-id": orgId,
      },
      data: JSON.stringify({
        site: {
          details: {
            address: address,
            city: city,
            state: state,
            zipcode: zip,
            polygon: [
              {
                latitude: lat,
                longitude: lng,
              },
            ],
          },
          name: name,
          parentId: null,
          timezone: timezone,
        },
      }),
    });

    if (response.status !== 200) {
      throw new Error(response?.data?.message);
    }
    const result = response?.data;
    return {
      strReturn: result.id,
    };
  } catch (error) {
    return {
      message: `${error}`,
    };
  }
}

export async function updateSite(
  orgId: string,
  siteId: string,
  formData: FormData,
) {
  const detail = {
    name: formData.get("name"),
    address: formData.get("address"),
    city: formData.get("city"),
    state: formData.get("state"),
    zip: formData.get("zip"),
    timezone: formData.get("timezone"),
  };
  const { name, address, city, state, zip, timezone } = detail;

  const token = Cookies.get("token");

  const lat = formData.get("latitude");
  const lng = formData.get("longitude");

  const dataToUpdate: any = {
    name: name,
    parentId: null,
  };

  if (address || city || state || zip || (lat && lng)) {
    dataToUpdate.details = {
      ...(address && { address }),
      ...(city && { city }),
      ...(state && { state }),
      ...(zip && { zipcode: zip }),
      ...(lat && lng && { polygon: [{ latitude: lat, longitude: lng }] }),
    };

    timezone && (dataToUpdate.timezone = timezone);
  }
  dataToUpdate.timezone = formData.get("timezone");

  try {
    const response = await sendHttpRequest({
      url: `${API_URL}/site/sites/${siteId}`,
      method: "put",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        "x-org-id": orgId,
      },
      data: JSON.stringify({ data: dataToUpdate }),
    });
    if (response.status !== 200) {
      throw new Error(response?.data?.message);
    }

    return {
      strReturn: "ok",
    };
  } catch (error) {
    return {
      message: `${error}`,
    };
  }
}

export async function deleteSite(orgId: string, siteId: string) {
  const token = Cookies.get("token");

  try {
    const response = await sendHttpRequest({
      url: `${API_URL}/site/sites/${siteId}`,
      method: "delete",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        "x-org-id": orgId,
      },
      data: JSON.stringify({}),
    });
    if (response.status !== 200) {
      throw new Error(response?.data?.message);
    }
  } catch (error) {
    throw new Error(`Failed to delete a site ${error}`);
  }
}

export async function createNvr(
  orgId: string,
  siteId: string,
  formData: FormData,
) {
  const validatedFields = CreateNvr.safeParse({
    macAddress: formData.get("macAddress"),
    serialNumber: formData.get("serialNumber"),
  });

  if (validatedFields.success === false) {
    return {
      errors: validatedFields.error.flatten().fieldErrors,
      message: "Missing Fields. Failed to Create NVR.",
    };
  }

  const { macAddress, serialNumber } = validatedFields.data;

  const token = Cookies.get("token");

  try {
    const response = await sendHttpRequest({
      url: `${API_URL}/device/nvrs/claim`,
      method: "post",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        "x-org-id": orgId,
      },
      data: JSON.stringify({
        siteId: siteId,
        macAddress: macAddress,
        sn: serialNumber,
      }),
    });
    if (response.status !== 200) {
      throw new Error(response?.data?.message);
    }
    const result = response?.data;

    return {
      strReturn: result,
    };
  } catch (error) {
    return {
      message: `${error}`,
    };
  }
}

export async function disclaimNvr(
  orgId: string,
  siteId: string,
  nvrId: string,
) {
  const token = Cookies.get("token");

  try {
    const response = await sendHttpRequest({
      url: `${API_URL}/device/nvrs/${nvrId}/disclaim`,
      method: "post",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        "x-org-id": orgId,
      },
      data: JSON.stringify({}),
    });
    if (response.status !== 200) {
      throw new Error(response?.data?.message);
    }
  } catch (error) {
    return {
      message: "Database Error: Failed to Disclaim NVR.",
    };
  }
}

export async function createCamera(
  orgId: string,
  siteId: string,
  nvrId: string,
  postData: string,
) {
  const validatedFields = CreateCamera.safeParse({});

  if (validatedFields.success === false) {
    return {
      errors: validatedFields.error.flatten().fieldErrors,
      message: "Missing Fields. Failed to Create NVR.",
    };
  }

  // const { macAddress, serialNumber } = validatedFields.data;

  const token = Cookies.get("token");

  const availablePostData = JSON.parse(postData);
  const newPostData = availablePostData.filter(
    (objCamera: any) => objCamera.status == "available",
  );

  try {
    const response = await sendHttpRequest({
      url: `${API_URL}/camera/cameras`,
      method: "post",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        "x-org-id": orgId,
      },
      data: JSON.stringify({
        data: newPostData,
        nvrId: nvrId,
        siteId: siteId,
      }),
    });
    const result = response?.data;

    if (response.status !== 200) {
      throw new Error(response?.data?.message);
    }
    return result;
  } catch (error) {
    return {
      message: `${error}`,
    };
  }
}

export async function deleteCamera(orgId: string, cameraId: string) {
  const token = Cookies.get("token");

  try {
    const response = await sendHttpRequest({
      url: `${API_URL}/camera/cameras/${cameraId}`,
      method: "delete",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        "x-org-id": orgId,
      },
      data: JSON.stringify({}),
    });
    if (response.status !== 200) {
      throw new Error(response?.data?.message);
    }
    return { message: "Deleted Camera." };
  } catch (error) {
    return {
      message: "Database Error: Failed to Delete Camera.",
    };
  }
}
export async function resetActivate(
  passSalt: string,
  token: string,
  formData: FormData,
) {
  const validatedFields = ActivateDealer.safeParse({
    // token: token,
    password: formData.get("password"),
    confirm_password: formData.get("confirm_password"),
  });

  if (validatedFields.success === false) {
    return {
      errors: validatedFields.error.flatten().fieldErrors,
      message: "Missing Fields. Failed to activate.",
    };
  }

  const { password, confirm_password } = validatedFields.data;
  if (confirm_password !== password) {
    return {
      message: "Two different passwords",
    };
  }

  const passHash = await argon2id(password, passSalt, 32);
  console.log("Hash: ", password, passHash);

  const response = await sendHttpRequest({
    url: `${API_URL}/auth/reset_password`,
    method: "post",
    headers: {
      "Content-Type": "application/json",
    },
    data: JSON.stringify({ password: passHash, token: token }),
  });
  if (response.status !== 200) {
    return { message: "Failed to activate user." };
  }
  const result = response?.data;
  return result;
}
export async function activateDealer(
  passSalt: string,
  token: string,
  formData: FormData,
) {
  let passHash;
  const authCode = formData.get("authCode");
  const ssoType = formData.get("ssoType");
  if (!authCode) {
    const validatedFields = ActivateDealer.safeParse({
      // token: token,
      password: formData.get("password"),
      confirm_password: formData.get("confirm_password"),
    });

    if (validatedFields.success === false) {
      return {
        errors: validatedFields.error.flatten().fieldErrors,
        message: "Missing Fields. Failed to activate.",
      };
    }

    const { password, confirm_password } = validatedFields.data;
    if (confirm_password !== password) {
      return {
        message: "Two different passwords",
      };
    }
    passHash = await argon2id(password, passSalt, 32);
    console.log("Hash: ", password, passHash);
  }
  const req = authCode
    ? { ssoCredential: { authCode, ssoType }, token }
    : { password: passHash, token };

  const response = await sendHttpRequest({
    url: `${API_URL}/auth/activate`,
    method: "post",
    headers: {
      "Content-Type": "application/json",
    },
    data: JSON.stringify(req),
  });
  if (response.status !== 200) {
    return { message: "Failed to activate user." };
  }
  const result = response?.data;
  return result;
}

export async function login(formData: FormData) {
  let email;
  let passHash;
  let lowerPassHash;
  const authCode = formData.get("authCode");
  const ssoType = formData.get("ssoType");
  if (!authCode) {
    const validatedFields = Signin.safeParse({
      email: formData.get("email"),
      password: formData.get("password"),
    });

    if (validatedFields.success === false) {
      return {
        errors: validatedFields.error.flatten().fieldErrors,
        message: "Missing Fields. Failed to Authenticate.",
      };
    }
    const { password } = validatedFields.data;
    email = validatedFields.data?.email;
    const passSalt = await argon2id(email, SALT, 16);
    const lowerPassSalt = await argon2id(
      email.toString().toLowerCase(),
      SALT,
      16,
    );
    lowerPassHash = await argon2id(password, lowerPassSalt, 32);
    passHash = await argon2id(password, passSalt, 32);
  }

  console.log("before Hash: ", passHash);
  const req = authCode
    ? { ssoCredential: { authCode, ssoType } }
    : { email, password: passHash, password_v2: lowerPassHash };
  console.log("req = ", req);
  const response = await sendHttpRequest({
    url: `${API_URL}/auth/login`,
    method: "post",
    headers: {
      "Content-Type": "application/json",
    },
    data: JSON.stringify(req),
  });
  if (response?.status !== 200) {
    throw new Error(response?.data?.message);
  }
  const result = response?.data;

  console.log("login resposne1 = ", result);
  const token = result.token;
  const accountName = result.user.organization.name;
  Cookies.set("orgId", result.user?.organization?.id, { expires: 1 });
  token &&
    Cookies.set("token", token, {
      expires: 1,
      domain: "." + window.location.hostname,
    });
  Cookies.set("accountName", accountName, { expires: 1 });
  Cookies.set("enableMonitoring", result.user.organization.enableMonitoring, {
    expires: 1,
  });
  Cookies.set("phoneNumber", result.user.phoneNumber, { expires: 1 });
  Cookies.set("name", result.user.firstName + " " + result.user.lastName);
  Cookies.set("userId", result.user.id);
  window.parent.postMessage({ token: token, type: "loginClick" }, "*");
  return result;
}

export async function sendResetEmail(formData: FormData) {
  const validatedFields = ResetEmail.safeParse({
    email: formData.get("email"),
  });
  if (validatedFields.success === false) {
    return {
      errors: validatedFields.error.flatten().fieldErrors,
      message: "Missing Fields. Failed to Authenticate.",
    };
  }
  const { email } = validatedFields.data;
  try {
    const response = await sendHttpRequest({
      url: `${API_URL}/auth/send_forget_password_email`,
      method: "post",
      headers: {
        "Content-Type": "application/json",
      },
      data: JSON.stringify({ email: email }),
    });
    if (response.status !== 200) {
      throw new Error(response?.data?.message);
    }
    const result = response?.data;
    return result;
  } catch (error) {
    console.error("Failed to fetch user:", error);
    throw new Error("Failed to fetch user.");
  }
}
export async function getCameraAlerts(
  orgId: string,
  cameraId: string,
  startAt: string,
  endAt: string,
  pageLast: string,
) {
  const token = Cookies.get("token");

  try {
    const response = await sendHttpRequest({
      url: `${API_URL}/alert/alerts?cameraIds=${cameraId}&startedAt=${startAt}&endedAt=${endAt}&paging.last=${pageLast}`,
      method: "get",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        "x-org-id": orgId,
      },
    });
    const result = response?.data;
    return result;
  } catch (error) {
    return {
      message: "Database Error: Failed to get Camera Alerts.",
    };
  }
}

export async function uploadFile(orgId: string, param: FormData) {
  const token = Cookies.get("token");
  try {
    const response = await sendHttpRequest({
      url: `${API_URL}/v2/service/software/upload`,
      method: "post",
      headers: {
        Authorization: `Bearer ${token}`,
        "x-org-id": orgId.toString(),
      },
      data: param,
    });

    if (response.status !== 200) {
      throw new Error(response?.data?.message);
    }

    return;
  } catch (error) {
    throw new Error("Failed to upload file.");
  }
}

export async function releaseVersion(
  orgId: string,
  service: string,
  version: string,
): Promise<any> {
  const token = Cookies.get("token");
  try {
    const response = await sendHttpRequest({
      url: `${API_URL}/v2/service/software/release`,
      method: "post",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        "x-org-id": orgId.toString(),
      },
      data: JSON.stringify({
        service: service,
        version: version,
      }),
    });
    if (response.status !== 200) {
      throw new Error(response?.data?.message);
    }
  } catch (error) {
    throw new Error("Failed to release Software.");
  }
}

export async function upgradeNvr(orgId: string): Promise<any> {
  const token = Cookies.get("token");
  try {
    const response = await sendHttpRequest({
      url: `${API_URL}/service/software/release`,
      method: "post",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        "x-org-id": orgId.toString(),
      },
    });
    const result = response?.data;
    return result;
  } catch (error) {
    throw new Error("Failed to upgrade Nvr.");
  }
}

export async function deleteNVRIPSpeaker(orgId: string, id: string) {
  const token = Cookies.get("token");

  try {
    const response = await sendHttpRequest({
      url: `${API_URL}/speaker/speakers/${id}`,
      method: "delete",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        "x-org-id": orgId,
      },
    });
    if (response.status !== 200) {
      throw new Error(response?.data?.message);
    }
    return { message: "Deleted IP Speaker." };
  } catch (error) {
    throw new Error(" Failed to Delete IP Speaker.");
  }
}

export async function revokeLicense(postData: object) {
  const token = Cookies.get("token");

  const response = await sendHttpRequest({
    url: `${API_URL}/license/terminate`,
    method: "post",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    data: JSON.stringify(postData),
  });
  const result = response?.data;
  return result;
}

export async function searchLocation(orgId: string) {
  const token = Cookies.get("token");

  const response = await sendHttpRequest({
    url: `${API_URL}/site/site_locations`,
    method: "get",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      "x-org-id": orgId,
    },
  });
  if (response.status !== 200) {
    throw new Error(response?.data?.message);
  }
  const result = response?.data;
  return result;
}
export async function createOrder(orgId: string, postData: object) {
  const token = Cookies.get("token");
  const response = await sendHttpRequest({
    url: `${API_URL}/camera/orders`,
    method: "post",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      "x-org-id": orgId,
    },
    data: JSON.stringify(postData),
  });
  if (response.status !== 200) {
    throw new Error(response?.data?.message);
  }
  const result = response?.data;
  return result;
}
export async function createOrgOrder(postData: object, orgId?: string) {
  const token = Cookies.get("token");
  const response = await sendHttpRequest({
    url: `${API_URL}/org/orders`,
    method: "post",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      "x-org-id": orgId,
    },
    data: JSON.stringify(postData),
  });
  if (response.status !== 200) {
    throw new Error(response?.data?.message);
  }
  const result = response?.data;
  return result;
}

export async function getOrderInfo(orderId: number) {
  const token = Cookies.get("token");

  const response = await sendHttpRequest({
    url: `${API_URL}/order/orders/${orderId}`,
    method: "get",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      //"x-org-id": orgId,
    },
  });
  if (response.status !== 200) {
    throw new Error(response?.data?.message);
  }
  const result = response?.data;
  return result;
}

export async function calculatePrice(data: any): Promise<any> {
  const token = Cookies.get("token");

  const response = await sendHttpRequest({
    url: `${API_URL}/license/price`,
    method: "post",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    data: JSON.stringify(data),
  });
  if (response.status !== 200) {
    throw new Error(response?.data?.message);
  }
  const result = response?.data;
  return result;
}

export async function batchObtainCredentials(
  orgId: string,
  sessionId: string,
  nvrIds: number[],
): Promise<any> {
  const token = Cookies.get("token");
  try {
    const response = await sendHttpRequest({
      url: `${API_URL}/webrtc/user/obtain_credentials_batch`,
      method: "post",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        "x-org-id": orgId.toString(),
      },
      data: JSON.stringify({
        deviceId: nvrIds,
        sessionId: sessionId,
        type: "nvr",
      }),
    });
    if (response.status !== 200) {
      throw new Error(response?.data?.message);
    }
    const result = response?.data;
    return result;
  } catch (error) {
    throw new Error("Failed to upgrade Nvr.");
  }
}

export async function shareAlert(
  token,
  alertId,
  emails,
  phoneNumbers,
  expiration,
  note,
) {
  const response = await sendHttpRequest({
    url: `${API_URL}/sharing/shares/public/alert`,
    method: "post",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      "x-agent": "web",
    },
    data: JSON.stringify({
      data: {
        alertId,
        contacts: {
          manualEmailNotifier: {
            emails,
          },
          manualSmsNotifier: {
            phoneNumbers,
          },
        },
        expiration: expiration ? parseInt(expiration) : undefined,
        note,
      },
    }),
  });
  if (response.status !== 200) {
    throw new Error(response?.data?.message);
  }
  return response?.data;
}
export async function partailUpdateSite(
  orgId: string,
  siteId: number,
  data: object,
) {
  const token = Cookies.get("token");

  try {
    const response = await sendHttpRequest({
      url: `${API_URL}/site/sites/${siteId}`,
      method: "patch",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        "x-org-id": orgId,
      },
      data: JSON.stringify(data),
    });
    if (response.status !== 200) {
      throw new Error(response?.data?.message);
    }
    return { message: "Partail Update Site." };
  } catch (error) {
    return {
      message: "Partail Update Site.",
    };
  }
}

export async function getDealerHealthAlertEmailList(orgId: string) {
  const token = Cookies.get("token");

  try {
    const response = await sendHttpRequest({
      url: `${API_URL}/alert/health/emails`,
      method: "get",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        "x-org-id": orgId,
      },
    });
    if (response.status !== 200) {
      throw new Error(response?.data?.message);
    }
    const result = response?.data;
    return result;
  } catch (error) {
    throw new Error(" Failed to get Dealer Health Alert Email List.");
  }
}

export async function getCamerasBySiteIds(
  orgId: string,
  siteIds: string,
  offSet: number,
  limit: number = 20,
) {
  const token = Cookies.get("token");
  try {
    const response = await sendHttpRequest({
      url: `${API_URL}/camera/cameras?siteIds=${siteIds}&page.limit=${limit}&page.offset=${offSet}`,
      method: "get",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        "x-org-id": orgId,
      },
    });
    const result = response?.data;
    if (response.status !== 200) {
      throw new Error(response?.data?.message);
    }
    return result;
  } catch (error) {
    return {
      message: `${error}`,
    };
  }
}

export async function updateDealerHealthAlertEmailList(
  orgId: string,
  data: object,
) {
  const token = Cookies.get("token");

  try {
    const response = await sendHttpRequest({
      url: `${API_URL}/alert/health/update_emails`,
      method: "post",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        "x-org-id": orgId,
      },
      data: JSON.stringify(data),
    });
    if (response.status !== 200) {
      throw new Error(response?.data?.message);
    }
    const result = response?.data;
    return result;
  } catch (error) {
    throw new Error(" Failed to update Dealer Health Alert Email List.");
  }
}
export async function updateDealerHealthAlertStatus(
  orgId: string,
  data: object,
) {
  const token = Cookies.get("token");

  try {
    const response = await sendHttpRequest({
      url: `${API_URL}/alert/health/update_read`,
      method: "post",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        "x-org-id": orgId,
      },
      data: JSON.stringify(data),
    });
    if (response.status !== 200) {
      throw new Error(response?.data?.message);
    }
    const result = response?.data;
    return result;
  } catch (error) {
    throw new Error(" Failed to update Dealer Health Alert status.");
  }
}

export async function getOrgUser(
  token: string,
  orgId: string,
  name: string,
  role: string,
  offset: number,
  limit: number,
) {
  const response = await sendHttpRequest({
    url: `${API_URL}/org/users?name=${name}&page.offset=${offset}&page.limit=${limit}&role=${role}`,
    method: "get",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      "x-agent": "web",
      "x-org-id": orgId,
    },
  });

  return response;
}

export async function createUser(token: string, orgId: string, userData: any) {
  const payload = {
    email: userData.email,
    firstName: userData.firstName,
    lastName: userData.lastName,
    phoneNumber: userData.phoneNumber,
    roleName: userData.roleName,
    siteIds: userData.siteIds || [], // Assuming siteIds can be empty or adjust as needed
    customers: userData.customers || [],
  };

  console.log("Creating user with data:", payload);

  const response = await sendHttpRequest({
    url: `${API_URL}/org/users`,
    method: "post",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      "x-agent": "web",
      "x-org-id": orgId,
    },
    data: JSON.stringify(payload),
  });
  if (response.status !== 200) {
    throw new Error(response?.data?.message);
  }

  return response?.data;
}

export async function updateUser(
  token: string,
  orgId: string,
  userId: string,
  userData: any,
) {
  console.log("Updating user with data:", userData);
  const response = await sendHttpRequest({
    url: `${API_URL}/org/users/${userId}`,
    method: "patch",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      "x-org-id": orgId,
      "x-agent": "web",
    },
    data: JSON.stringify(userData),
  });

  if (response.status !== 200) {
    throw new Error(response?.data?.message);
  }

  return response?.data;
}

export async function deleteUser(token: string, orgId: string, userId: string) {
  const response = await sendHttpRequest({
    url: `${API_URL}/org/users/${userId}`,
    method: "delete",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      "x-org-id": orgId,
      "x-agent": "web",
    },
  });

  if (response.status !== 200) {
    throw new Error(response?.data?.message);
  }

  return response?.data;
}

export async function getUser(token: string, orgId: string, userId: string) {
  const response = await sendHttpRequest({
    method: "get",
    url: `${API_URL}/org/users/${userId}`,
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      "x-org-id": orgId,
      "x-agent": "web",
    },
  });

  if (response.status !== 200) {
    throw new Error(response?.data?.message);
  }

  return response?.data;
}

export async function fetchSites(token: string, orgId: string) {
  const response = await sendHttpRequest({
    url: `${API_URL}/site/tree`,
    method: "get",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      "x-org-id": orgId,
      "x-agent": "web",
    },
  });

  if (response.status !== 200) {
    throw new Error(response?.data?.message);
  }

  const data = response?.data;
  return data.sites || []; // Ensure it returns an array
}

export async function addAlertNote(
  token: string,
  alertId: string,
  note: string,
) {
  const response = await sendHttpRequest({
    url: `${API_URL}/alert/notes`,
    method: "post",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      "x-agent": "web",
    },
    data: JSON.stringify({ alertId, note }),
  });

  if (response.status !== 200) {
    throw new Error(response?.data?.message);
  }

  return response?.data;
}

export async function deleteAlertNote(token: string, noteId: string) {
  const response = await sendHttpRequest({
    url: `${API_URL}/alert/notes/${noteId}`,
    method: "delete",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      "x-agent": "web",
    },
  });
  if (response.status !== 200) {
    throw new Error(response?.data?.message);
  }

  return response.data;
}

export async function findAlertNotes(token: string, alertId: string) {
  const response = await sendHttpRequest({
    url: `${API_URL}/alert/notes?alertId=${alertId}`,
    method: "get",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      "x-agent": "web",
    },
  });

  if (response.status !== 200) {
    throw new Error(response?.data?.message);
  }

  return response?.data;
}
export function getCurrentUserInfo() {
  return sendHttpRequest({
    method: "get",
    url: "/auth/me",
  });
}
